/**
 * Auth API module
 *
 * Base URLs:
 *   local dev  → http://localhost:43302
 *   testnet    → https://test-explorer.zetrix.com/service  (relative: /service)
 *   production → https://explorer.zetrix.com/service       (relative: /service)
 *
 * Response envelope: { data: {...}, meta: { code: 0, message: '...' } }
 *   code 0       = success
 *   code 1xxxxx  = validation / business error
 *   code 400000  = system error
 */

import axios from 'axios'
import { handleSession403 } from './sessionGuard'

// ── Base URL ─────────────────────────────────────────────────────────────────
// Keep auth requests relative so they use the dev proxy in config/index.js,
// matching the existing /service-based API modules.
const AUTH_BASE = '/service'

// ── Token storage (localStorage) ─────────────────────────────────────────────
// TODO (Security): Tokens are currently stored in localStorage.
// This is susceptible to XSS — any script that runs on the page can read these values.
// Recommended long-term fix: have the backend set tokens in httpOnly, SameSite=Strict
// cookies so they are never accessible from JavaScript.
// Short-term mitigation: enforce a strict Content Security Policy on the server.
const TOKEN_KEY = 'explorer_auth'

export function getStoredTokens () {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_KEY)) || {}
  } catch {
    return {}
  }
}

export function saveTokens ({ accessToken, refreshToken }) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify({ accessToken, refreshToken }))
}

export function clearTokens () {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem('explorer_user')
}

// ── Axios instance ────────────────────────────────────────────────────────────
const http = axios.create({
  baseURL: AUTH_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
})

// ── Request interceptor: attach Bearer token ──────────────────────────────────
// Skip auth endpoints — they are public and should not receive the (possibly
// expired) access token, which some servers reject with 403 before processing
// the request body (e.g. the refresh-token body).
http.interceptors.request.use(config => {
  const isPublicAuthPath = (config.url || '').startsWith('/auth/')
  if (!isPublicAuthPath) {
    const { accessToken } = getStoredTokens()
    if (accessToken) {
      config.headers = config.headers || {}
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }
  }
  return config
})

// ── Response interceptor: 401 → refresh → retry ───────────────────────────────
let _isRefreshing = false
let _refreshQueue = []

function _processQueue (error, token) {
  // If error is truthy every queued promise is rejected, preventing any queued
  // request from retrying with a stale token (race-condition safety: confirmed).
  _refreshQueue.forEach(p => (error ? p.reject(error) : p.resolve(token)))
  _refreshQueue = []
}

http.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config || {}

    const isAuthEndpoint =
      (original.url || '').includes('/auth/refresh-token') ||
      (original.url || '').includes('/auth/login')

    // 403 Forbidden — show session-expired dialog (Continue = refresh token + retry,
    // Cancel = clear session + redirect to login).
    // Skip for /auth/ endpoints (refresh-token, login) to avoid infinite loops.
    if (error.response && error.response.status === 403) {
      if (isAuthEndpoint) {
        clearTokens()
        window.location.href = '/login'
        return new Promise(() => {})
      }
      return handleSession403(error, http)
    }

    if (error.response && error.response.status === 401 && !original._retry && !isAuthEndpoint) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject })
        }).then(token => {
          original.headers['Authorization'] = `Bearer ${token}`
          return http(original)
        })
      }

      original._retry = true
      _isRefreshing = true
      const { refreshToken } = getStoredTokens()

      if (!refreshToken) {
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        const res = await axios.post(`${AUTH_BASE}/auth/refresh-token`, { refreshToken })
        const body = res.data
        if (body && body.meta && body.meta.code === 0) {
          const { accessToken, refreshToken: newRefresh } = body.data
          saveTokens({ accessToken, refreshToken: newRefresh })
          _processQueue(null, accessToken)
          original.headers['Authorization'] = `Bearer ${accessToken}`
          return http(original)
        }
        throw new Error('Refresh failed')
      } catch (err) {
        _processQueue(err, null)
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(err)
      } finally {
        _isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// ── Response helper ───────────────────────────────────────────────────────────
function handleResponse (res) {
  const body = res.data
  if (!body || !body.meta) throw new Error('Invalid server response')
  if (body.meta.code !== 0) {
    const err = new Error(body.meta.message || 'Request failed')
    err.code = body.meta.code
    throw err
  }
  return body.data
}

// ── Auth endpoints ────────────────────────────────────────────────────────────

/**
 * Step 1 of registration — sends OTP to email.
 * @returns { maskedEmail, otpType, expiresAt, remainingAttempts, resendCooldownSeconds }
 */
export function register (payload) {
  // payload: { username, email, password, confirmPassword, agreeToTerms }
  return http.post('/auth/register', payload).then(handleResponse)
}

/**
 * Step 2 of registration — verify OTP, returns tokens + user.
 * @returns { accessToken, refreshToken, expiresIn, tokenType, user }
 */
export function verifyOtp (email, otpCode) {
  return http.post('/auth/verify-otp', { email, otpCode }).then(handleResponse)
}

/**
 * Resend registration OTP (60-second cooldown).
 * @returns { maskedEmail, otpType, expiresAt, remainingAttempts, resendCooldownSeconds }
 */
export function resendOtp (email) {
  return http.post('/auth/resend-otp', { email }).then(handleResponse)
}

/**
 * Login — returns tokens + user.
 * @returns { accessToken, refreshToken, expiresIn, tokenType, user }
 */
export function login (identifier, password) {
  return http.post('/auth/login', { identifier, password }).then(handleResponse)
}

/**
 * Forgot password — sends reset link to email (always succeeds for security).
 * @returns { message, maskedEmail, success }
 */
export function forgotPassword (email) {
  return http.post('/auth/forgot-password', { email }).then(handleResponse)
}

/**
 * Reset password — validates token from email link and sets new password.
 * @returns { message, success }
 */
export function resetPassword (token, newPassword, confirmPassword) {
  return http.post('/auth/reset-password', { token, newPassword, confirmPassword }).then(handleResponse)
}

/**
 * Refresh access token using refresh token.
 * @returns { accessToken, refreshToken, expiresIn, tokenType, user }
 */
export function refreshAccessToken (refreshToken) {
  return http.post('/auth/refresh-token', { refreshToken }).then(handleResponse)
}

/**
 * Logout — invalidates current session. Requires Bearer token (auto-attached).
 * @returns { message, success }
 */
export function logout () {
  return http.post('/auth/logout').then(handleResponse)
}

// ── User endpoints ────────────────────────────────────────────────────────────

/**
 * Get current user profile + email change status.
 * @returns UserResponse (see API docs)
 */
export function getProfile () {
  return http.get('/user/profile').then(handleResponse)
}

/**
 * Request email change — changes email immediately (unverified), sends verification link.
 * @returns { message, success }
 */
export function updateEmail (newEmail, password) {
  return http.post('/user/update-email', { newEmail, password }).then(handleResponse)
}

/**
 * Confirm email change via token from verification link (public endpoint — no auth needed).
 * @returns { message, success }
 */
export function verifyEmailChange (token) {
  // No Bearer token needed — token is in the body
  return axios
    .post(`${AUTH_BASE}/user/verify-email-change`, { token }, { headers: { 'Content-Type': 'application/json' } })
    .then(handleResponse)
}

/**
 * Change password. Requires Bearer token.
 * @returns { message, success }
 */
export function changePassword (currentPassword, newPassword, confirmPassword) {
  return http
    .post('/user/change-password', { currentPassword, newPassword, confirmPassword })
    .then(handleResponse)
}

/**
 * Soft delete account (30-day grace period). Requires Bearer token.
 * @returns { message, success }
 */
export function deleteAccount (password, reason) {
  return http
    .post('/user/delete-account', { password, confirmDelete: true, reason: reason || '' })
    .then(handleResponse)
}

// ── Default export (for convenience) ─────────────────────────────────────────
export default {
  // Token helpers
  getStoredTokens,
  saveTokens,
  clearTokens,
  // Auth
  register,
  verifyOtp,
  resendOtp,
  login,
  forgotPassword,
  resetPassword,
  refreshAccessToken,
  logout,
  // User
  getProfile,
  updateEmail,
  verifyEmailChange,
  changePassword,
  deleteAccount
}
